import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { api, useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Trophy, Medal, Award, Loader2, Volume2, Mic, HelpCircle, Film, PenLine, Sparkles, Crown, Star, Flame } from 'lucide-react';
import { toast } from 'sonner';

const LeaderboardPage = () => {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moduleType, setModuleType] = useState('');

  useEffect(() => {
    fetchLeaderboard();
  }, [moduleType]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const url = moduleType 
        ? `/leaderboard?module_type=${moduleType}`
        : '/leaderboard';
      const res = await api.get(url);
      setLeaderboard(res.data);
    } catch (err) {
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="h-6 w-6 text-amber-500 drop-shadow-lg" />;
      case 2:
        return <Medal className="h-5 w-5 text-slate-400 drop-shadow" />;
      case 3:
        return <Medal className="h-5 w-5 text-orange-400 drop-shadow" />;
      default:
        return <span className="text-slate-500 font-bold text-lg">{rank}</span>;
    }
  };

  const getRankClass = (rank) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-amber-100 via-yellow-50 to-amber-100 dark:from-amber-900/40 dark:via-yellow-900/30 dark:to-amber-900/40 border-2 border-amber-300 dark:border-amber-600 shadow-lg shadow-amber-200/50 dark:shadow-amber-900/30';
      case 2:
        return 'bg-gradient-to-r from-slate-100 via-gray-50 to-slate-100 dark:from-slate-800/60 dark:via-gray-800/40 dark:to-slate-800/60 border-2 border-slate-300 dark:border-slate-600 shadow-md';
      case 3:
        return 'bg-gradient-to-r from-orange-100 via-amber-50 to-orange-100 dark:from-orange-900/30 dark:via-amber-900/20 dark:to-orange-900/30 border-2 border-orange-300 dark:border-orange-700 shadow-md';
      default:
        return 'bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800';
    }
  };

  const moduleIcons = {
    tts: Volume2,
    stt: Mic,
    mcq: HelpCircle,
    vmcq: Film,
    ftb: PenLine
  };

  const moduleColors = {
    tts: { bg: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-600 dark:text-cyan-400', border: 'border-cyan-300 dark:border-cyan-700' },
    stt: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-300 dark:border-emerald-700' },
    mcq: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-600 dark:text-amber-400', border: 'border-amber-300 dark:border-amber-700' },
    vmcq: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-600 dark:text-purple-400', border: 'border-purple-300 dark:border-purple-700' },
    ftb: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-600 dark:text-orange-400', border: 'border-orange-300 dark:border-orange-700' }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
        {/* Premium Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 p-8 text-white text-center">
          <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,transparent,white)]" />
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
          <div className="relative z-10">
            <div className="inline-flex p-4 rounded-full bg-white/20 backdrop-blur-sm mb-4 shadow-lg">
              <Trophy className="h-12 w-12 text-white drop-shadow-lg" />
            </div>
            <h1 className="text-3xl font-bold mb-2 flex items-center justify-center gap-2">
              Leaderboard
              <Sparkles className="h-6 w-6 text-yellow-200 animate-pulse" />
            </h1>
            <p className="text-white/80">
              Compete with your classmates and climb the ranks!
            </p>
          </div>
        </div>

        {/* Filter Tabs - Premium Style */}
        <Tabs defaultValue="" onValueChange={setModuleType}>
          <TabsList className="grid grid-cols-6 max-w-3xl mx-auto bg-slate-100 dark:bg-slate-800/50 p-1 rounded-xl">
            <TabsTrigger value="" data-testid="tab-all" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 data-[state=active]:shadow-md rounded-lg font-medium">
              <Star className="h-3 w-3 mr-1" />All
            </TabsTrigger>
            <TabsTrigger value="tts" data-testid="tab-tts" className="data-[state=active]:bg-cyan-100 dark:data-[state=active]:bg-cyan-900/50 data-[state=active]:text-cyan-700 dark:data-[state=active]:text-cyan-300 rounded-lg font-medium">
              <Volume2 className="h-3 w-3 mr-1" />TTS
            </TabsTrigger>
            <TabsTrigger value="stt" data-testid="tab-stt" className="data-[state=active]:bg-emerald-100 dark:data-[state=active]:bg-emerald-900/50 data-[state=active]:text-emerald-700 dark:data-[state=active]:text-emerald-300 rounded-lg font-medium">
              <Mic className="h-3 w-3 mr-1" />STT
            </TabsTrigger>
            <TabsTrigger value="mcq" data-testid="tab-mcq" className="data-[state=active]:bg-amber-100 dark:data-[state=active]:bg-amber-900/50 data-[state=active]:text-amber-700 dark:data-[state=active]:text-amber-300 rounded-lg font-medium">
              <HelpCircle className="h-3 w-3 mr-1" />MCQ
            </TabsTrigger>
            <TabsTrigger value="ftb" data-testid="tab-ftb" className="data-[state=active]:bg-orange-100 dark:data-[state=active]:bg-orange-900/50 data-[state=active]:text-orange-700 dark:data-[state=active]:text-orange-300 rounded-lg font-medium">
              <PenLine className="h-3 w-3 mr-1" />FTB
            </TabsTrigger>
            <TabsTrigger value="vmcq" data-testid="tab-vmcq" className="data-[state=active]:bg-purple-100 dark:data-[state=active]:bg-purple-900/50 data-[state=active]:text-purple-700 dark:data-[state=active]:text-purple-300 rounded-lg font-medium">
              <Film className="h-3 w-3 mr-1" />VMCQ
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Leaderboard Card - Premium */}
        <Card className="overflow-hidden border-2 border-slate-200 dark:border-slate-700 shadow-xl">
          <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 border-b border-slate-200 dark:border-slate-700">
            <CardTitle className="flex items-center gap-2">
              <Flame className="h-5 w-5 text-orange-500" />
              Top Performers
              {moduleType && (
                <span className={`ml-2 px-3 py-1 text-xs font-bold rounded-full ${moduleColors[moduleType]?.bg} ${moduleColors[moduleType]?.text} uppercase shadow-sm`}>
                  {moduleType}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
              </div>
            ) : leaderboard.length === 0 ? (
              <div className="text-center py-12 px-4">
                <Trophy className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500 font-medium">No data available yet</p>
                <p className="text-sm text-slate-400">Complete some exercises to appear on the leaderboard!</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {leaderboard.map((entry, idx) => {
                  const isCurrentUser = entry.id === user?.id;
                  return (
                    <div 
                      key={entry.id} 
                      className={`p-4 flex items-center gap-4 transition-all hover:bg-slate-50 dark:hover:bg-slate-800/50 ${isCurrentUser ? 'bg-indigo-50/50 dark:bg-indigo-900/20' : ''} ${getRankClass(entry.rank)}`}
                      data-testid={`leaderboard-row-${idx}`}
                    >
                      {/* Rank Badge */}
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold ${
                        entry.rank === 1 ? 'bg-gradient-to-br from-amber-400 to-amber-600 text-white shadow-lg shadow-amber-200/50' :
                        entry.rank === 2 ? 'bg-gradient-to-br from-slate-300 to-slate-500 text-white shadow-md' :
                        entry.rank === 3 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white shadow-md' :
                        'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                      }`}>
                        {entry.rank <= 3 ? getRankIcon(entry.rank) : entry.rank}
                      </div>

                      {/* Name & Attempts */}
                      <div className="flex-1 min-w-0">
                        <p className={`font-semibold truncate ${isCurrentUser ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-900 dark:text-white'}`}>
                          {entry.name}
                          {isCurrentUser && (
                            <span className="ml-2 px-2 py-0.5 text-xs bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-400 rounded-full">You</span>
                          )}
                          {entry.rank === 1 && <Sparkles className="inline h-4 w-4 ml-1 text-amber-500" />}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {entry.total_attempts} attempts
                        </p>
                      </div>

                      {/* Score */}
                      <div className="text-right">
                        <p className={`text-2xl font-bold ${
                          entry.average_score >= 80 ? 'text-emerald-600 dark:text-emerald-400' :
                          entry.average_score >= 50 ? 'text-amber-600 dark:text-amber-400' :
                          'text-slate-600 dark:text-slate-400'
                        }`}>
                          {entry.average_score}%
                        </p>
                        <p className="text-xs text-slate-500">Avg Score</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Your Position - Premium Card */}
        {!loading && leaderboard.length > 0 && (
          <Card className="overflow-hidden border-0 shadow-2xl">
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm mb-1">Your Position</p>
                  <p className="text-4xl font-bold text-white flex items-center gap-2">
                    #{leaderboard.find(e => e.id === user?.id)?.rank || '-'}
                    {leaderboard.find(e => e.id === user?.id)?.rank === 1 && <Crown className="h-8 w-8 text-yellow-300" />}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-white/80 text-sm mb-1">Your Average Score</p>
                  <p className="text-4xl font-bold text-white">
                    {leaderboard.find(e => e.id === user?.id)?.average_score || 0}%
                  </p>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default LeaderboardPage;
